import {useState, useEffect} from 'react';
import {Autocomplete, CircularProgress, TextField} from '@mui/material';

type FuzzyAutocompleteProps = {
  label: string;
  inputValue: string;
  options: string[];
  loading: boolean;
  isTooShort: boolean;
  noOptionsText: string;
  onInputChange: (value: string) => void;
};

const FuzzyAutocomplete = ({
  label,
  inputValue,
  options,
  loading,
  isTooShort,
  noOptionsText,
  onInputChange,
}: FuzzyAutocompleteProps) => {
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const [searchText, setSearchText] = useState(inputValue);

  // Sync searchText with external inputValue changes (e.g., reset)
  useEffect(() => {
    setSearchText(inputValue);
    // Clear selection if input is cleared externally
    if (!inputValue) {
      setSelectedValue(null);
    }
  }, [inputValue]);

  return (
    <Autocomplete
      disableClearable
      openOnFocus
      forcePopupIcon={false}
      value={selectedValue || ''}
      inputValue={searchText}
      options={options}
      loading={loading}
      onInputChange={(_, newInput, reason) => {
        if (reason === 'input') {
          setSearchText(newInput);
          setSelectedValue(null); // Clear selection when user types
          onInputChange(newInput);
        } else if (reason === 'clear') {
          setSearchText('');
          setSelectedValue(null);
          onInputChange('');
        }
      }}
      onChange={(_, newValue) => {
        if (newValue) {
          setSelectedValue(newValue);
          setSearchText(newValue);
          onInputChange(newValue);
        } else {
          setSelectedValue(null);
        }
      }}
      noOptionsText={
        isTooShort ? 'Type at least 3 characters to search' : noOptionsText
      }
      renderOption={(props, option) => (
        <li {...props} key={option}>
          {option}
        </li>
      )}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          type='search'
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color='inherit' size={16} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      sx={{minWidth: 200, flex: 1}}
    />
  );
};

export default FuzzyAutocomplete;
